import {Button, ButtonGroup, Classes, Colors, Dialog, Intent} from "@blueprintjs/core";
import {ButtonContainer, FieldSet, IDataTableColumn, useGridState, useSearchApi} from "nsitools-react";
import * as React from "react";

import {DogApi, DogPedigreeApi, DogPedigreeGridDto, DogPedigreeSearch} from "../../../../../../api";
import {CustomDataTable, DeleteButton, EditButton, ExportButton} from "../../../../../../components";
import {useAuth, useGlobalData} from "../../../../../../contexts";
import {useApiService, useDeleteApi, useTl} from "../../../../../../hooks";
import {ETLCodes} from "../../../../../../locales";
import {DogPedigreePanel} from "./DogPedigreePanel";
import {Link} from "react-router-dom";
import {ERoutes} from "../../../../../../ERoutes";
import {DogCurrentOwnerSelectionPanel} from "../People/DogCurrentOwnerSelectionPanel";

export interface IDogPedigreesListProps {
    dogId: number;
    editMode: boolean;
    refresh: () => void;
}

export const DogPedigreesList: React.FunctionComponent<IDogPedigreesListProps> = ({dogId, editMode, refresh}) => {
    const {t} = useTl();
    const {userPermissions} = useAuth();
    const api = useApiService(DogPedigreeApi);
    const dogApi = useApiService(DogApi);
    const {currentLanguage} = useGlobalData();
    const [dialogOpened, setDialogOpened] = React.useState(false);
    const [sobjState, setsobjState] = React.useState<any>();
    const [pedigreeId, setpedigreeId] = React.useState(0);
    const tableState = useGridState<any>({
        serverMode: true,
        enablePagination: true,
        enableFilter: true,
        availablePageSizes: [5],
        pageSize: 5,
    });
    const [selectedReperintPedigreeId, setSelectedReperintPedigreeId] = React.useState<number>(undefined);
    const [generateDuplicatePedigreeLoading, setGenerateDuplicatePedigreeLoading] = React.useState(false);
    const searchFunction = React.useCallback(
        (sObj?: DogPedigreeSearch) => {
            setsobjState(DogPedigreeSearch.fromJS({...sObj, dogId: dogId, languageId: currentLanguage.languageId}));
            return api.baseSearch(
                DogPedigreeSearch.fromJS({...sObj, dogId: dogId, languageId: currentLanguage.languageId})
            );
        },
        [api, currentLanguage.languageId, dogId]
    );

    const {search, loading} = useSearchApi<any, any>({
        searchFunction,
        tableState,
        initialSearch: true,
        trimSearchObjectStringValues: true,
    });

    const {deleteItem} = useDeleteApi<number>(
        React.useMemo(
            () => ({
                deleteApiFn: (data) => api.deleteDogPedigree(data),
                onDeletedFn: () => {
                    refresh();
                    search();
                },
            }),
            [api, refresh, search]
        )
    );

    const addItemFunction = React.useCallback((pId: number) => {
        setpedigreeId(pId);
        setDialogOpened(true);
    }, []);

    const generateDuplicatePedigree = React.useCallback(
        async (personId: number, pedigreeId: number) => {
            setSelectedReperintPedigreeId(undefined)
            setGenerateDuplicatePedigreeLoading(true);
            await dogApi.generateDuplicatePedigree(dogId, personId, pedigreeId);
            setGenerateDuplicatePedigreeLoading(false);

            refresh();
        },
        [api, dogId, refresh]
    );

    const columns = React.useMemo<IDataTableColumn[]>(
        () => [
            {
                computed: true,
                disableSorting: true,
                fieldName: "actions",
                autoFitContent: true,
                hidden: !editMode,
                render: (row: DogPedigreeGridDto) => (
                    <ButtonContainer>
                        {editMode &&
                            userPermissions.some(
                                (p) =>
                                    p.module.toUpperCase() === "DOG" &&
                                    p.permission.toUpperCase() === "E" &&
                                    p.tab.toUpperCase() === "ADDPEDIGREE"
                            ) && (
                                <EditButton
                                    minimal={true}
                                    onClick={() => {
                                        addItemFunction(row.pedigreeId);
                                    }}
                                />
                            )}

                        <DeleteButton minimal={true} onDelete={() => deleteItem(row.pedigreeId)}/>
                        {row.isBelgian && <Button icon={"print"} intent={"primary"} minimal={true}
                                                  loading={generateDuplicatePedigreeLoading}
                                                  onClick={() => setSelectedReperintPedigreeId(row.pedigreeId)}/>}
                    </ButtonContainer>
                ),
            },
            {
                header: () => t(ETLCodes.Country),
                fieldName: "country",
                render: (row: DogPedigreeGridDto) => (
                    <Link target="_blank" to={`${ERoutes.country}/${row.countryId}/detail/view`}>
                        {row.country}
                    </Link>
                ),
            },
            {
                header: () => t(ETLCodes.Pedigree),
                fieldName: "prefix",
                render: (row: DogPedigreeGridDto) => `${row.prefix ?? ""}-${row.identifier ?? ""}`,
            },
            {
                header: () => t(ETLCodes.AttributionDate),
                fieldName: "attributionDate",
            },
        ],
        [addItemFunction, deleteItem, editMode, t, userPermissions, generateDuplicatePedigreeLoading, setSelectedReperintPedigreeId]
    );

    React.useEffect(() => {
        search();
    }, [currentLanguage, search]);

    return (
        <>
            <FieldSet
                title={t(ETLCodes.Pedigrees)}
                rightElement={
                    <ButtonGroup>
                        <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState}/>
                        {editMode &&
                            userPermissions.some(
                                (p) =>
                                    p.module.toUpperCase() === "DOG" &&
                                    p.permission.toUpperCase() === "E" &&
                                    p.tab.toUpperCase() === "ADDPEDIGREE"
                            ) && (
                                <EditButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        addItemFunction(null);
                                    }}
                                    text={t(ETLCodes.General_Add)}
                                    intent={Intent.PRIMARY}
                                />
                            )}
                    </ButtonGroup>
                }
            >
                <CustomDataTable tableState={tableState} loading={loading} columns={columns}/>
            </FieldSet>
            <Dialog
                title={t(ETLCodes.PedigreeSelection)}
                isOpen={dialogOpened}
                onClose={() => setDialogOpened(false)}
                canEscapeKeyClose={true}
                canOutsideClickClose={true}
                isCloseButtonShown={true}
                className={Classes.DIALOG}
                style={{width: 1000, background: Colors.WHITE}}
            >
                <div className={Classes.DIALOG_BODY} style={{height: 200}}>
                    <DogPedigreePanel
                        dogId={dogId}
                        onSaved={() => {
                            setDialogOpened(false);
                            refresh();
                            search();
                        }}
                        pedigreeId={pedigreeId}
                    />
                </div>
            </Dialog>
            <Dialog
                isOpen={!!selectedReperintPedigreeId}
                title={t(ETLCodes.SelectContact)}
                className={""}
                canOutsideClickClose={true}
                style={{width: 1200, background: Colors.WHITE}}
                onClose={() => {
                    setSelectedReperintPedigreeId(undefined);
                }}
            >
                <div className={Classes.DIALOG_BODY}>
                    <DogCurrentOwnerSelectionPanel
                        dogId={dogId}
                        onPersonSelected={(personId) => generateDuplicatePedigree(personId, selectedReperintPedigreeId)}
                    />
                </div>
            </Dialog>
        </>
    );
};
